import NotificationsNoneRounded from '@mui/icons-material/NotificationsNoneRounded';
import { workspaceApi } from '../../api/workspaces';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import BookmarkBorderRounded from '@mui/icons-material/BookmarkBorderRounded';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import EditNoteRounded from '@mui/icons-material/EditNoteRounded';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import CreditCardOutlined from '@mui/icons-material/CreditCardOutlined';
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import CategoryOutlined from '@mui/icons-material/CategoryOutlined';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link as RouterLink, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Drawer,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import WorkOutlineRounded from '@mui/icons-material/WorkOutlineRounded';
import PeopleOutlineRounded from '@mui/icons-material/PeopleOutlineRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import PersonOutlineRounded from '@mui/icons-material/PersonOutlineRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import MenuRounded from '@mui/icons-material/MenuRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import useResponsive from '../../hooks/useResponsive';
import { useAuth } from '../../contexts/AuthContext';
import { fetchUnreadCount } from '../../api/portal';
import { PATH_DASHBOARD, PATH_PAGE } from '../../routes/paths';
import BrandLockup from '../../components/BrandLockup';
import AccountMenu from '../main/AccountMenu';
import FloatingAssistant from '../../sections/ai/FloatingAssistant';

const NAV_WIDTH = 238;

export default function DashboardLayout() {
  const { account, isEmployer, isAdmin, logout } = useAuth();
  const baseTheme = useTheme();
  const isLight = isEmployer || isAdmin;
  const isDesktop = useResponsive('up', 'lg');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notices, setNotices] = useState(0);
  const [search, setSearch] = useState('');
  const paths = isAdmin
    ? {
        root: PATH_DASHBOARD.admin.root,
        profile: PATH_DASHBOARD.admin.settings,
        messages: PATH_DASHBOARD.admin.jobs,
        applications: PATH_DASHBOARD.admin.jobs,
      }
    : isEmployer
      ? PATH_DASHBOARD.employer
      : PATH_DASHBOARD.employee;
  const theme = useMemo(
    () =>
      createTheme(baseTheme, {
        palette: {
          primary: {
            main: isLight ? '#0875E9' : '#4338D9',
            light: isLight ? '#DCECFF' : '#E9E7FF',
            dark: isLight ? '#055AB5' : '#3326B3',
            contrastText: '#fff',
          },
          text: { primary: '#182034', secondary: '#7A8190' },
          divider: '#ECEEF3',
          background: { default: '#F8F9FC', paper: '#FFFFFF', neutral: '#F7F8FB' },
        },
        typography: {
          fontFamily: 'Inter, sans-serif',
          h1: { fontFamily: 'Inter, sans-serif' },
          h2: { fontFamily: 'Inter, sans-serif' },
          h3: { fontFamily: 'Inter, sans-serif' },
          h4: { fontFamily: 'Inter, sans-serif', fontSize: '1.75rem', letterSpacing: '-1px' },
          h5: { fontFamily: 'Inter, sans-serif' },
          h6: { fontFamily: 'Inter, sans-serif', fontSize: '1rem', letterSpacing: '-0.3px' },
        },
        components: {
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: isLight ? 20 : 16,
                border: '1px solid #ECEEF3',
                boxShadow: '0 2px 4px rgba(24,32,52,0.015)',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: { borderRadius: isLight ? 24 : 9 },
              containedPrimary: {
                boxShadow: 'none',
                backgroundColor: isLight ? '#0875E9' : '#4338D9',
                '&:hover': {
                  backgroundColor: isLight ? '#055AB5' : '#3326B3',
                  boxShadow: 'none',
                },
              },
            },
          },
          MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 10 } } },
        },
      }),
    [baseTheme, isLight]
  );

  useEffect(() => {
    setIsNavOpen(false);
  }, [pathname, isDesktop]);
  useEffect(() => {
    if (isAdmin) {
      setUnread(0);
      return;
    }
    let cancelled = false;
    const check = () =>
      fetchUnreadCount()
        .then((count) => {
          if (!cancelled) setUnread(count);
        })
        .catch(() => undefined);
    check();
    const timer = window.setInterval(check, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [pathname, isAdmin]);

  useEffect(() => {
    let cancelled = false;
    const check = () =>
      workspaceApi
        .get<{ unread: number }>('/notifications')
        .then((data) => {
          if (!cancelled) setNotices(data.unread);
        })
        .catch(() => undefined);
    check();
    const timer = window.setInterval(check, 30000);
    window.addEventListener('notifications-changed', check);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('notifications-changed', check);
    };
  }, [pathname]);
  const navItems: {
    title: string;
    path: string;
    icon: React.ReactNode;
    end?: boolean;
    badge?: boolean;
  }[] = isAdmin
    ? [
        {
          title: 'Overview',
          path: PATH_DASHBOARD.admin.root,
          icon: <DashboardOutlined />,
          end: true,
        },
        { title: 'Users', path: PATH_DASHBOARD.admin.users, icon: <PeopleOutlineRounded /> },
        { title: 'Employers', path: PATH_DASHBOARD.admin.employers, icon: <WorkOutlineRounded /> },
        { title: 'Job approvals', path: PATH_DASHBOARD.admin.jobs, icon: <FactCheckOutlined /> },
        { title: 'Job import agent', path: PATH_DASHBOARD.admin.imports, icon: <WorkOutlineRounded /> },
        { title: 'Candidate database', path: PATH_DASHBOARD.admin.talent, icon: <PeopleOutlineRounded /> },
        { title: 'Categories', path: PATH_DASHBOARD.admin.categories, icon: <CategoryOutlined /> },
        {
          title: 'Plans & payments',
          path: PATH_DASHBOARD.admin.payments,
          icon: <CreditCardOutlined />,
        },
        { title: 'Reports', path: PATH_DASHBOARD.admin.reports, icon: <BarChartRounded /> },
        { title: 'Settings', path: PATH_DASHBOARD.admin.settings, icon: <SettingsOutlined /> },
      ]
    : isEmployer
      ? [
          { title: 'Dashboard', path: paths.root, icon: <DashboardOutlined />, end: true },
          {
            // No `end` — posting and editing live under /jobs, so this stays
            // lit on those screens. Posting is reached from the button on the
            // My jobs page rather than its own nav entry.
            title: 'My jobs',
            path: PATH_DASHBOARD.employer.jobs,
            icon: <WorkOutlineRounded />,
          },
          {
            title: 'AI manager',
            path: PATH_DASHBOARD.employer.aiManager,
            icon: <AutoAwesomeOutlined />,
          },
          {
            title: 'Candidate search',
            path: PATH_DASHBOARD.employer.candidates,
            icon: <SearchRounded />,
          },
          {
            title: 'Applicant tracking',
            path: PATH_DASHBOARD.employer.pipeline,
            icon: <AccountTreeOutlined />,
          },
          { title: 'Applications', path: paths.applications, icon: <PeopleOutlineRounded /> },
          {
            title: 'Interviews',
            path: PATH_DASHBOARD.employer.interviews,
            icon: <CalendarMonthOutlined />,
          },
          {
            title: 'Messages',
            path: paths.messages,
            icon: <ChatBubbleOutlineRounded />,
            badge: true,
          },
          {
            title: 'Plans & billing',
            path: PATH_DASHBOARD.employer.billing,
            icon: <CreditCardOutlined />,
          },
          { title: 'Company profile', path: paths.profile, icon: <PersonOutlineRounded /> },
        ]
      : [
          { title: 'Dashboard', path: paths.root, icon: <DashboardOutlined />, end: true },
          { title: 'Find jobs', path: PATH_PAGE.jobs, icon: <SearchRounded /> },
          {
            title: 'Job matches',
            path: PATH_DASHBOARD.employee.matches,
            icon: <AutoAwesomeOutlined />,
          },
          { title: 'My applications', path: paths.applications, icon: <DescriptionOutlined /> },
          {
            title: 'Saved jobs',
            path: PATH_DASHBOARD.employee.savedJobs,
            icon: <BookmarkBorderRounded />,
          },
          {
            title: 'Interviews',
            path: PATH_DASHBOARD.employee.interviews,
            icon: <CalendarMonthOutlined />,
          },
          {
            title: 'Messages',
            path: paths.messages,
            icon: <ChatBubbleOutlineRounded />,
            badge: true,
          },
          { title: 'Profile & CV', path: paths.profile, icon: <PersonOutlineRounded /> },
          {
            title: 'Resume builder',
            path: PATH_DASHBOARD.employee.resumeBuilder,
            icon: <EditNoteRounded />,
          },
        ];
  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    navigate(`${PATH_PAGE.jobs}${search.trim() ? `?q=${encodeURIComponent(search.trim())}` : ''}`);
  };
  const navContent = (
    <Stack sx={{ height: 1, px: 1.5, py: 2, color: isLight ? 'text.primary' : '#fff' }}>
      <Box
        component={RouterLink}
        to={PATH_PAGE.root}
        aria-label="Job Victory home"
        sx={{ textDecoration: 'none', px: 0.5, mb: 2 }}
      >
        <BrandLockup light={!isLight} />
      </Box>
      <Typography
        sx={{
          px: 2,
          mb: 1.5,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '1.4px',
          color: isLight ? '#9A9FAD' : '#82899C',
        }}
      >
        {isAdmin ? 'SUPER ADMIN' : isEmployer ? 'HIRING WORKSPACE' : 'YOUR WORKSPACE'}
      </Typography>
      <List sx={{ p: 0 }}>
        {navItems.map((item) => (
          <ListItemButton
            key={item.path}
            component={NavLink}
            to={item.path}
            end={item.end}
            onClick={() => setIsNavOpen(false)}
            sx={{
              borderRadius: isLight ? '28px' : '9px',
              py: 0.65,
              px: 2,
              mb: 0.35,
              color: isLight ? '#747A88' : '#C7CCDA',
              '&.active': { bgcolor: 'primary.main', color: '#fff' },
              '&:hover': { bgcolor: isLight ? '#F0F6FE' : '#1D2540' },
              '&.active:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 34, '& svg': { fontSize: 20 } }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.title}
              primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
            />
            {item.badge && unread > 0 && (
              <Badge color="error" badgeContent={unread} sx={{ mr: 1 }} />
            )}
          </ListItemButton>
        ))}
      </List>
      <Box sx={{ flexGrow: 1, minHeight: 24 }} />
      <Box
        sx={{
          mx: 0.5,
          p: 2,
          mb: 2,
          bgcolor: isLight ? '#F0F7FF' : '#EAE9F8',
          borderRadius: '13px',
          color: '#1B2440',
        }}
      >
        <Typography
          sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '1px', color: 'primary.main', mb: 1 }}
        >
          {isAdmin ? 'COMMUNITY FIRST' : isEmployer ? 'BUILD YOUR TEAM' : 'YOUR NEXT CHAPTER'}
        </Typography>
        <Typography
          sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.4, letterSpacing: '-0.5px' }}
        >
          {isAdmin
            ? 'A healthy community starts here.'
            : isEmployer
              ? 'Great teams start with a great hire.'
              : 'A little progress. A big opportunity.'}
        </Typography>
        <Typography sx={{ fontSize: 11, color: '#727A8B', lineHeight: 1.6, mt: 1, mb: 1.5 }}>
          {isAdmin
            ? 'Keep opportunities and people moving forward.'
            : isEmployer
              ? 'Connect with people ready to make an impact.'
              : 'Keep your profile ready for your next opportunity.'}
        </Typography>
        <Button
          fullWidth
          component={RouterLink}
          to={
            isAdmin
              ? PATH_DASHBOARD.admin.reports
              : isEmployer
                ? PATH_DASHBOARD.employer.newJob
                : paths.profile
          }
          endIcon={<ArrowForwardRounded sx={{ fontSize: '16px !important' }} />}
          sx={{ bgcolor: '#fff', fontSize: 11, justifyContent: 'space-between', px: 1.5 }}
        >
          {isAdmin ? 'View platform reports' : isEmployer ? 'Post a new role' : 'Update my profile'}
        </Button>
      </Box>
      <Button
        component={RouterLink}
        to="/dashboard/support"
        color="inherit"
        startIcon={<HelpOutlineRounded />}
        sx={{
          justifyContent: 'flex-start',
          px: 2,
          fontWeight: 400,
          fontSize: 12,
          color: isLight ? '#747A88' : '#ADB5C8',
        }}
      >
        Help & support
      </Button>
      <Button
        onClick={() => {
          logout();
          navigate(PATH_PAGE.root);
        }}
        color="inherit"
        startIcon={<LogoutRounded />}
        sx={{
          justifyContent: 'flex-start',
          px: 2,
          fontWeight: 400,
          fontSize: 12,
          color: isLight ? '#747A88' : '#ADB5C8',
        }}
      >
        Sign out
      </Button>
    </Stack>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          bgcolor: isLight ? '#fff' : '#F8F9FC',
          p: { xs: 0, lg: 1.5 },
          gap: 1.5,
        }}
      >
        {isDesktop ? (
          <Box
            component="nav"
            aria-label="Dashboard navigation"
            sx={{
              width: NAV_WIDTH,
              flexShrink: 0,
              position: 'sticky',
              top: 12,
              height: 'calc(100vh - 24px)',
              overflowY: 'auto',
              bgcolor: isLight ? '#fff' : '#0B1020',
              borderRadius: '16px',
            }}
          >
            {navContent}
          </Box>
        ) : (
          <Drawer
            open={isNavOpen}
            onClose={() => setIsNavOpen(false)}
            PaperProps={{ sx: { width: NAV_WIDTH, bgcolor: isLight ? '#fff' : '#0B1020' } }}
          >
            {navContent}
          </Drawer>
        )}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            border: isLight ? '1px solid #ECEEF3' : 'none',
            borderRadius: { xs: 0, lg: '24px' },
            bgcolor: isLight ? '#FDFEFF' : '#F8F9FC',
          }}
        >
          <Stack
            component="header"
            direction="row"
            alignItems="center"
            spacing={{ xs: 1, sm: 2 }}
            sx={{
              mx: { xs: 2, md: 3.5 },
              height: 78,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {!isDesktop && (
              <IconButton aria-label="Open menu" onClick={() => setIsNavOpen(true)}>
                <MenuRounded />
              </IconButton>
            )}
            <Box
              component="form"
              onSubmit={handleSearch}
              role="search"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}
            >
              <IconButton type="submit" aria-label="Search jobs" size="small">
                <SearchRounded sx={{ fontSize: 20, color: '#9299A6' }} />
              </IconButton>
              <InputBase
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search jobs, skills, companies..."
                inputProps={{ 'aria-label': 'Search jobs by title or skill' }}
                sx={{
                  fontSize: 12,
                  width: 1,
                  '& input::placeholder': { opacity: 1, color: '#9BA0AC' },
                }}
              />
            </Box>
            <IconButton
              component={RouterLink}
              to="/dashboard/notifications"
              aria-label={`Notifications${notices ? `, ${notices} unread` : ''}`}
              sx={{ width: 36, height: 36, bgcolor: '#fff', border: '1px solid #ECEEF3' }}
            >
              <Badge badgeContent={notices} color="error">
                <NotificationsNoneRounded sx={{ fontSize: 19, color: '#798194' }} />
              </Badge>
            </IconButton>
            <IconButton
              component={RouterLink}
              to={paths.messages}
              aria-label={
                isAdmin ? 'Job approvals' : `Messages${unread ? `, ${unread} unread` : ''}`
              }
              sx={{ bgcolor: '#fff', border: '1px solid #ECEEF3', width: 36, height: 36 }}
            >
              <Badge badgeContent={unread} color="error">
                <ChatBubbleOutlineRounded sx={{ fontSize: 17, color: '#798194' }} />
              </Badge>
            </IconButton>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AccountMenu />
              <Box sx={{ display: { xs: 'none', sm: 'block' }, maxWidth: 150 }}>
                <Typography noWrap sx={{ fontSize: 12, fontWeight: 600 }}>
                  {account?.name}
                </Typography>
                <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                  {isAdmin ? 'Super administrator' : isEmployer ? 'Hiring team' : 'Job seeker'}
                </Typography>
              </Box>
            </Stack>
          </Stack>
          <Box
            component="main"
            sx={{ px: { xs: 2, md: 3.5 }, py: 3.5, maxWidth: 1600, mx: 'auto' }}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>
      {isEmployer && <FloatingAssistant key={account?.id} />}
    </ThemeProvider>
  );
}
